'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  Phone,
  Menu,
  X,
  ChevronDown,
  MapPin,
  Calendar,
  FlaskConical,
  Package,
  Home,
  Building2,
  Users,
  Mail,
  LayoutDashboard,
  Heart,
  UserCircle,
} from 'lucide-react';

const navLinks = [
  { label: 'Home', href: '/', icon: Home },
  {
    label: 'Services',
    href: '#',
    icon: FlaskConical,
    children: [
      { label: 'Advanced Pathology', href: '#', icon: FlaskConical },
      { label: 'ECG & 2D Echo', href: '#', icon: FlaskConical },
      { label: 'Ultrasound & X-Ray', href: '#', icon: FlaskConical },
      { label: 'Home Collection', href: '#', icon: Home },
      { label: 'Corporate Checkups', href: '#', icon: Building2 },
    ],
  },
  { label: 'Health Packages', href: '/health-packages', icon: Package },
  { label: 'About Us', href: '/about', icon: Users },
  { label: 'Doctors', href: '#', icon: Users },
  { label: 'Contact Us', href: '/contact', icon: Mail },
  { label: 'Book Appointment', href: '/book-appointment', icon: Calendar },
];

export default function Header() {
  const pathname = usePathname();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <>
      {/* Top bar */}
      <div className="hero-gradient text-white text-sm py-2 hidden md:block">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5">
              <MapPin size={13} />
              H-56, Sector 51, Noida
            </span>
          </div>
          <div className="flex items-center gap-6">
            <a href="mailto:info@51pluslab.com" className="flex items-center gap-1.5 hover:text-sky-200 transition-colors">
              <Mail size={13} />
              info@51pluslab.com
            </a>
            <span className="text-sky-200">|</span>
            <a href="tel:+919818986535" className="flex items-center gap-1.5 hover:text-sky-200 transition-colors">
              <Phone size={13} />
              +91 98189 86535
            </a>
            <span className="text-sky-200">|</span>
            <span className="font-medium">Mon–Sun: 7:00 AM – 8:00 PM</span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header
        className={`sticky top-0 z-50 bg-white border-b border-border transition-all duration-300 ${
          isScrolled ? 'shadow-lg' : 'shadow-sm'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-6 xl:px-8">
          <div className="flex items-center justify-between h-16 gap-2">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2 flex-shrink-0 group">
              <div className="relative">
                <AppLogo size={52} className="drop-shadow-md" />
              </div>
              <div className="flex flex-col hidden sm:flex">
                <span className="font-extrabold text-lg leading-tight text-foreground tracking-tight group-hover:text-primary transition-colors">
                  51 Plus Lab
                </span>
                <span className="text-[10px] text-muted-foreground font-semibold leading-none tracking-wide uppercase">
                  NABL Accredited Diagnostics
                </span>
                <span className="text-[9px] text-primary font-bold leading-none tracking-wide uppercase mt-0.5">
                  NABL Accreditation No: MC-64-15
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden xl:flex items-center gap-0.5 flex-1 justify-center">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={`nav-${link.label}`} className="relative group">
                    <button
                      className={`flex items-center gap-0.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-muted hover:text-primary whitespace-nowrap ${
                        isActive(link.href) ? 'text-primary bg-secondary' : 'text-foreground'
                      }`}
                      onClick={() => setServicesOpen(!servicesOpen)}
                    >
                      {link.label}
                      <ChevronDown size={12} className="transition-transform group-hover:rotate-180" />
                    </button>
                    <div className="absolute top-full left-0 mt-1 w-52 bg-card border border-border rounded-xl shadow-modal opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                      <div className="p-1.5">
                        {link.children.map((child) => (
                          <Link
                            key={`nav-child-${child.label}`}
                            href={child.href}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                          >
                            <child.icon size={14} className="text-primary" />
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={`nav-${link.label}`}
                    href={link.href}
                    className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-muted hover:text-primary whitespace-nowrap ${
                      isActive(link.href) ? 'text-primary bg-secondary font-semibold' : 'text-foreground'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </nav>

            {/* CTA + Mobile toggle */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <a
                href="tel:+919818986535"
                className="hidden lg:flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 transition-colors whitespace-nowrap"
              >
                <Phone size={13} />
                +91 98189 86535
              </a>
              <Link
                href="/corporate-login"
                className="hidden lg:inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary border border-border hover:border-primary/40 rounded-lg px-2.5 py-1.5 transition-colors whitespace-nowrap"
              >
                <LayoutDashboard size={13} />
                Corporate
              </Link>
              <Link
                href="/patient-login"
                className="hidden lg:inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary border border-border hover:border-primary/40 rounded-lg px-2.5 py-1.5 transition-colors whitespace-nowrap"
              >
                <Heart size={13} />
                Patient Portal
              </Link>
              <Link
                href="/patient-dashboard"
                className="hidden lg:inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary border border-border hover:border-primary/40 rounded-lg px-2.5 py-1.5 transition-colors whitespace-nowrap"
              >
                <UserCircle size={13} />
                My Dashboard
              </Link>
              <Link
                href="/book-appointment"
                className="hidden md:inline-flex btn-primary text-xs py-2 px-4 whitespace-nowrap"
              >
                <Calendar size={13} />
                Book Now
              </Link>
              <button
                className="xl:hidden p-2 rounded-lg hover:bg-muted transition-colors"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-border bg-card animate-slide-up">
            {/* Mobile logo strip */}
            <div className="px-4 py-3 border-b border-border flex items-center gap-3 bg-secondary/30">
              <AppLogo size={56} />
              <div>
                <div className="font-extrabold text-base text-foreground">51 Plus Lab</div>
                <div className="text-xs text-muted-foreground">NABL Accredited Diagnostics</div>
              </div>
            </div>
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={`mobile-nav-${link.label}`}>
                    <button
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted hover:text-primary transition-colors"
                      onClick={() => setServicesOpen(!servicesOpen)}
                    >
                      {link.label}
                      <ChevronDown size={14} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {servicesOpen && (
                      <div className="ml-4 mt-1 space-y-1">
                        {link.children.map((child) => (
                          <Link
                            key={`mobile-child-${child.label}`}
                            href={child.href}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                            onClick={() => setMobileOpen(false)}
                          >
                            <child.icon size={13} className="text-primary" />
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={`mobile-nav-${link.label}`}
                    href={link.href}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-primary ${
                      isActive(link.href) ? 'text-primary bg-secondary' : 'text-foreground'
                    }`}
                    onClick={() => setMobileOpen(false)}
                  >
                    <link.icon size={15} className="text-primary" />
                    {link.label}
                  </Link>
                )
              )}
              <div className="pt-3 border-t border-border">
                <Link
                  href="/corporate-login"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-primary transition-colors mb-2"
                  onClick={() => setMobileOpen(false)}
                >
                  <LayoutDashboard size={15} className="text-primary" />
                  Corporate Login
                </Link>
                <Link
                  href="/patient-login"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-primary transition-colors mb-2"
                  onClick={() => setMobileOpen(false)}
                >
                  <Heart size={15} className="text-primary" />
                  Patient Portal
                </Link>
                <Link
                  href="/patient-dashboard"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-primary transition-colors mb-2"
                  onClick={() => setMobileOpen(false)}
                >
                  <UserCircle size={15} className="text-primary" />
                  My Dashboard
                </Link>
                <Link
                  href="/book-appointment"
                  className="btn-primary w-full justify-center"
                  onClick={() => setMobileOpen(false)}
                >
                  <Calendar size={15} />
                  Book Appointment
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}